/**
 * Consent store for the lbx-training SDK pathway.
 *
 * Source of truth: `circle_members.consent_*` columns on the signed-in
 * user's self-row, exposed via `self-member-service.ts`. This module
 * is a thin in-memory cache + subscribe layer over that service so the
 * consent UI (`TrainingCaptureConsent.tsx`) and the capture coordinator
 * (`training-capture-coordinator.ts`) stay in lock-step without each
 * doing its own round-trip on every render.
 *
 * Public API is intentionally unchanged from the prior localStorage
 * implementation so existing call sites don't move:
 *   - `getTrainingConsent()` — sync read of the in-memory snapshot
 *   - `setTrainingConsent(patch)` — async-ish write-through to Supabase
 *   - `subscribeTrainingConsent(fn)` — push updates to listeners
 *   - `canCaptureWithGrants(g)` — pure gate predicate
 *   - `hydrateTrainingConsentFromServer(memberId)` — new: called by
 *     the sign-in hook after `fetchOrCreateSelfMember` resolves
 *
 * NOT the same as `circle_members.consent_*` reads in
 * `lib/training/use-training-capture.ts` — that hook owns the
 * in-house TrainingCaptureService pathway and reads the same row
 * directly. Both pipelines now share the row; the two layers stay
 * separate by design so neither has to compete for ownership.
 */

import {
  readSelfMemberConsent,
  updateSelfMemberConsent,
  type SelfMemberConsent,
} from './self-member-service'
import { createLogger } from '@/lib/monitoring/logger'

const log = createLogger('training-consent-store')

export const CURRENT_CONSENT_VERSION = 'v1-2026-05-16'

export interface TrainingConsentGrants {
  audio: boolean
  camera: boolean
  training: boolean
  bipaAck: boolean
  version: string
}

const DEFAULT_GRANTS: TrainingConsentGrants = {
  audio: false,
  camera: false,
  training: false,
  bipaAck: false,
  version: CURRENT_CONSENT_VERSION,
}

type Listener = (grants: TrainingConsentGrants) => void

const listeners = new Set<Listener>()

// In-memory snapshot. UI reads this synchronously so toggles don't
// flicker between server round-trips. Hydrated on sign-in by the
// layout-level `hydrateTrainingConsentFromServer(memberId)` call;
// stays at `DEFAULT_GRANTS` (fail-closed) before that runs or when
// the user is signed out.
let snapshot: TrainingConsentGrants = { ...DEFAULT_GRANTS }
let boundMemberId: string | null = null

function fromServer(c: SelfMemberConsent): TrainingConsentGrants {
  return {
    audio: c.audio,
    camera: c.camera,
    training: c.training,
    bipaAck: c.bipaAck,
    version: c.version || CURRENT_CONSENT_VERSION,
  }
}

function fanOut(next: TrainingConsentGrants): void {
  snapshot = next
  for (const fn of listeners) {
    try {
      fn(next)
    } catch (err) {
      // listener failure must not break the fan-out
      log.warn('consent listener threw', { err: String(err) })
    }
  }
}

/**
 * Sync read of the in-memory snapshot. Call sites that need the
 * authoritative server state should also call
 * `hydrateTrainingConsentFromServer(memberId)` once on mount.
 */
export function getTrainingConsent(): TrainingConsentGrants {
  return snapshot
}

/**
 * Hydrate the in-memory snapshot from the user's `circle_members`
 * row. Called once by the sign-in hook after
 * `fetchOrCreateSelfMember()` resolves; safe to call again after
 * a fresh sign-in. Subsequent calls with the same memberId no-op
 * unless `force=true`.
 */
export async function hydrateTrainingConsentFromServer(
  memberId: string,
  opts: { force?: boolean } = {},
): Promise<TrainingConsentGrants> {
  if (!opts.force && boundMemberId === memberId) return snapshot
  boundMemberId = memberId
  try {
    const server = await readSelfMemberConsent(memberId)
    const next = fromServer(server)
    fanOut(next)
    return next
  } catch (err) {
    log.warn('hydrate failed; keeping last-known snapshot', {
      err: String(err),
      memberId,
    })
    return snapshot
  }
}

/**
 * Clear the in-memory cache. Call on sign-out so a different user
 * signing in next doesn't briefly see the previous user's grants.
 */
export function resetTrainingConsentCache(): void {
  boundMemberId = null
  fanOut({ ...DEFAULT_GRANTS })
}

/**
 * Apply a consent patch. Writes through to `circle_members` if a
 * memberId is bound; updates the in-memory snapshot + fans out to
 * listeners optimistically so the UI doesn't lag the click. If the
 * server write fails the snapshot is reverted and listeners are
 * fanned the old value so the toggle visibly snaps back.
 *
 * No-op when no memberId is bound (signed-out / pre-hydration);
 * returns the unchanged snapshot in that case.
 */
export function setTrainingConsent(
  partial: Partial<TrainingConsentGrants>,
): TrainingConsentGrants {
  if (!boundMemberId) {
    log.warn('setTrainingConsent called before hydrate; ignoring', { partial })
    return snapshot
  }
  const before = snapshot
  const optimistic: TrainingConsentGrants = {
    ...before,
    ...partial,
    version: CURRENT_CONSENT_VERSION,
  }
  fanOut(optimistic)

  const memberId = boundMemberId
  // Fire-and-forget write-through. Revert on failure so the UI
  // reflects the server's actual state.
  updateSelfMemberConsent(memberId, {
    audio: partial.audio,
    camera: partial.camera,
    training: partial.training,
    bipaAck: partial.bipaAck,
    version: CURRENT_CONSENT_VERSION,
  }).catch((err) => {
    log.warn('write-through failed; reverting snapshot', {
      err: String(err),
      memberId,
    })
    fanOut(before)
  })
  return optimistic
}

export function subscribeTrainingConsent(fn: Listener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/**
 * "Capture is allowed" iff the user explicitly granted training + at least
 * one modality (audio or camera). BIPA acknowledgement is only required
 * for users we believe are in a BIPA-equivalent region; the coordinator
 * inspects this separately.
 */
export function canCaptureWithGrants(g: TrainingConsentGrants): boolean {
  return g.training && (g.audio || g.camera)
}
